import { getGA4SessionId, getGAClientId } from 'lib/gtm/config'
import { reportSentryError } from 'lib/sentry'
import { logAnalyticsEvents } from 'shared/analytics'
import { isSecurityError } from 'shared/utils/errors'

const DEFAULT_BATCH_SIZE = 5

const DEFAULT_BATCH_TIMEOUT = 2000

export class Queue {
  private eventQueue: (() => void)[] = []
  private priorityQueue: (() => void)[] = []
  private BATCH_SIZE = DEFAULT_BATCH_SIZE
  private BATCH_TIMEOUT = DEFAULT_BATCH_TIMEOUT
  private batchTimeout: NodeJS.Timeout | null = null

  constructor(
    batchSize: number = DEFAULT_BATCH_SIZE,
    batchTimeout: number = DEFAULT_BATCH_TIMEOUT
  ) {
    this.BATCH_SIZE = batchSize
    this.BATCH_TIMEOUT = batchTimeout
    this.sendBatch = this.sendBatch.bind(this)
  }

  push(fn: () => void, priority: boolean): void {
    if (priority) {
      // Insert after existing priority items so execution order = push order (FIFO).
      this.priorityQueue.push(fn)
    } else {
      this.eventQueue.push(fn)
    }

    const totalQueueLength = this.priorityQueue.length + this.eventQueue.length

    if (totalQueueLength >= this.BATCH_SIZE) {
      this.sendBatch()
    } else if (!this.batchTimeout) {
      this.batchTimeout = setTimeout(() => this.sendBatch(), this.BATCH_TIMEOUT)
    }
  }

  private sendBatch(): void {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout)
      this.batchTimeout = null
    }

    const batch = [...this.priorityQueue, ...this.eventQueue]

    if (batch.length === 0) return

    this.priorityQueue = []
    this.eventQueue = []

    batch.forEach(fn => {
      try {
        fn()
      } catch (error) {
        console.error('Queue callback failed:', error)

        // Storage access blocked (Safari private mode) — expected, skip Sentry.
        if (isSecurityError(error)) return

        reportSentryError(error, 'Queue sendBatch callback failed')
      }
    })
  }

  flush(): void {
    this.sendBatch()
  }
}

declare global {
  interface Window {
    dataLayer: unknown[]
  }
}

const queueAnalytics = new Queue(5, 10)

export const queueAnalyticsEvent = (dataEvent: unknown, priority = false) => {
  queueAnalytics.push(() => {
    if (!dataEvent) return

    window.dataLayer = window.dataLayer || []
    const event = {
      ...dataEvent,
      session_id: getGA4SessionId(),
      client_id: getGAClientId()
    }

    logAnalyticsEvents(event)
    window.dataLayer.push(event)
  }, priority)
}

export const flushAnalyticsEvents = () => {
  queueAnalytics.flush()
}
